import type { MediaImageFragment } from '~/api/graphql/generated'

export type ResponsiveImageData = MediaImageFragment['fieldMediaImage']

export interface ResponsiveImageSlice {
  width: number
  height: number
  url: string
}

export interface BuildResponsiveImageSlicesOptions {
  maxOriginalWidth?: number
}

export const RESPONSIVE_IMAGE_DEFAULT_MAX_WIDTH = 3000

export const RESPONSIVE_IMAGE_DERIVATIVE_KEYS = [
  'small',
  'smallRetina',
  'medium',
  'mediumRetina',
  'large',
  'largeRetina',
  'xlarge',
  'xxlarge',
] as const

type DerivativeKey = (typeof RESPONSIVE_IMAGE_DERIVATIVE_KEYS)[number]

type MaybeResponsiveSlice =
  | {
      width?: number | null
      height?: number | null
      url?: string | null
    }
  | null
  | undefined

type ImageDataRecord = Partial<Record<DerivativeKey, MaybeResponsiveSlice>> & {
  original?: MaybeResponsiveSlice
  width?: number | null
  height?: number | null
  url?: string | null
}

const toSlice = (slice: MaybeResponsiveSlice): ResponsiveImageSlice | undefined => {
  if (!slice) return undefined

  const { width, height, url } = slice
  if (width == null || height == null || !url) return undefined

  return {
    width,
    height,
    url,
  }
}

export const buildResponsiveImageSlices = (
  imageData: ResponsiveImageData | null | undefined,
  options: BuildResponsiveImageSlicesOptions = {},
): ResponsiveImageSlice[] => {
  if (!imageData) return []

  const { maxOriginalWidth = RESPONSIVE_IMAGE_DEFAULT_MAX_WIDTH } = options
  const dataRecord = imageData as ImageDataRecord

  const originalFallback = toSlice({
    width: imageData.width,
    height: imageData.height,
    url: imageData.url,
  })

  const derivatives = RESPONSIVE_IMAGE_DERIVATIVE_KEYS.reduce<ResponsiveImageSlice[]>((acc, key) => {
    const slice = toSlice(dataRecord[key])
    if (!slice) return acc
    if (originalFallback && slice.width === originalFallback.width) return acc

    acc.push(slice)
    return acc
  }, [])

  const originalDerivative = toSlice(dataRecord.original)
  const preferredOriginal =
    originalDerivative && originalDerivative.width <= maxOriginalWidth
      ? originalDerivative
      : originalFallback && originalFallback.width !== undefined && originalFallback.width <= maxOriginalWidth
        ? originalFallback
        : undefined

  if (!preferredOriginal) return derivatives

  return [...derivatives.filter((slice) => slice.width !== preferredOriginal.width), preferredOriginal]
}
